import swal from 'sweetalert2';
import { translateBackendKey } from '../../commons/utils/Utils';

/**
 * VEC-3316 — el flujo de solicitud de stock, compartido entre pantallas.
 *
 * Vive acá y no dentro de un componente porque lo usan DOS pantallas con reglas distintas:
 *
 *   - la APERTURA del correctivo (`CorrectivosAbm`): la solicitud es OPCIONAL, así que primero
 *     pregunta y solo si el usuario dice que sí abre el formulario de cantidades;
 *   - la carga del PRESUPUESTO DETALLADO (`TicketPresupuestoDetallado`): es BLOQUEANTE — sin
 *     stock no se puede guardar—, así que va directo al formulario, sin pregunta previa.
 *
 * Son funciones y no componentes React a propósito: SweetAlert2 vive fuera del árbol de React y
 * las dos pantallas necesitan `await`ear una decisión en medio de un handler, no renderizar algo.
 *
 * ⚠️ EL CONTENIDO SE ARMA CON DOM Y NO CON UN STRING HTML. `swal html:` con interpolación es un
 * XSS sistémico en este repo —en este módulo ya se corrigió una vez, en VEC-3312— y acá se
 * interpolarían descripciones de ítems, que las carga un usuario. Con `createElement` y
 * `textContent` no hay forma de que una descripción se ejecute.
 *
 * ⚠️ `type:` y no `icon:`: sweetalert2 está en la 7.33 y `icon` recién existe desde la v9.
 */

/**
 * VEC-3905 — lo que un ticket YA tiene pedido, indexado por ítem del catálogo.
 *
 * Entra la respuesta de `GET /api/inventario/solicitudes?ticket=` y sale el mapa que consume
 * `pedirCantidadesSolicitud()` para marcar las filas.
 *
 * SOLO LAS ABIERTAS. El endpoint devuelve también las cerradas a propósito — el historial explica
 * qué pasó con cada repuesto —, pero lo que bloquea un pedido nuevo es únicamente una línea
 * vigente: una rechazada o ya cubierta libera la combinación. El predicado es el mismo que el del
 * backend, por el estado y no por `cantidadPendiente`: una línea aprobada parcialmente sigue abierta.
 *
 * @param {Array} solicitudes filas tal como las devuelve el endpoint
 * @returns {Object} itemId => { detalleId, cantidad, cantidadPendiente, estado }
 */
export const solicitudesAbiertasPorItem = (solicitudes) => {
	const abiertas = {};

	(solicitudes || []).forEach((fila) => {
		if (!fila || !fila.item || fila.estado !== 'PENDIENTE') return;

		// La primera gana: el backend garantiza una sola abierta por ítem, y si por una carrera
		// hubiera dos, se señala la más vieja — que es la que el usuario ya conoce.
		if (abiertas[fila.item.id]) return;

		abiertas[fila.item.id] = {
			detalleId: fila.id,
			cantidad: fila.cantidad,
			cantidadPendiente: fila.cantidadPendiente,
			estado: fila.estado,
		};
	});

	return abiertas;
};

/** Qué hacer después de que el usuario resolvió el modal de faltantes. */
export const CANCELADO = 'cancelado';
export const SOLO_SOLICITUD = 'solo_solicitud';
export const GUARDAR_BORRADOR = 'guardar_borrador';

/**
 * La decisión que toma el presupuesto detallado cuando el modal de faltantes se resuelve.
 *
 * Vive acá y no dentro del componente para poder probarla: `TicketPresupuestoDetallado` arrastra
 * jQuery, xlsx y media pantalla, y montarlo para verificar tres ramas sería desproporcionado. Mismo
 * criterio que `obligatoriosPresupuesto` y `mezclaValidacionAutomatica`.
 *
 * Las tres ramas:
 *
 *   - `CANCELADO` — el usuario cerró el modal. No se envía ni se guarda nada: es la salida para
 *     corregir las cantidades o sacar el repuesto del presupuesto.
 *   - `GUARDAR_BORRADOR` — el caso normal. El presupuesto se guarda como borrador y el trabajo
 *     cargado no se pierde, aunque el gate del backend impida presupuestarlo sin stock.
 *   - `SOLO_SOLICITUD` — el cliente NO tiene el flag de borrador. Sin borrador no hay dónde caer, así
 *     que queda el comportamiento viejo: la solicitud se envía y el presupuesto no se guarda.
 *
 * @param {Array|null} cantidades lo que devolvió el modal (null = canceló)
 * @param {boolean} borradorHabilitado `tickets.presupuesto.borrador.habilitado`
 */
export const accionTrasSolicitudStock = (cantidades, borradorHabilitado) => {
	if (cantidades === null || cantidades === undefined) return CANCELADO;

	return borradorHabilitado ? GUARDAR_BORRADOR : SOLO_SOLICITUD;
};

/** Lo que falta de un ítem para cubrir lo pedido. Nunca negativo. */
export const faltanteDe = (item) => {
	const disponible = item ? item.stockDisponible : null;
	const necesaria = Number((item && item.cantidad) || 0);

	if (disponible === null || disponible === undefined) return necesaria;

	return Math.max(necesaria - disponible, 0);
};

/**
 * ¿Este ítem pide más de lo que hay?
 *
 * `stockDisponible` en null NO cuenta como faltante: es "no se puede saber" (alta masiva,
 * correctivo de llanta, móvil sin base) y ahí no hay contra qué comparar. Solo el 0 y los números
 * concretos disparan el aviso.
 */
export const tieneFaltante = (item) =>
	item &&
	item.stockDisponible !== null &&
	item.stockDisponible !== undefined &&
	Number(item.cantidad || 0) > item.stockDisponible;

/** Resultados posibles de la pregunta previa. */
export const SOLICITAR = 'solicitar';
export const SIN_SOLICITUD = 'sin_solicitud';
export const CANCELAR = 'cancelar';

/**
 * Paso PREVIO, solo para las pantallas donde la solicitud es opcional.
 *
 * ⚠️ TRES SALIDAS CON DOS BOTONES. `showDenyButton` recién existe desde la v10, así que el tercer
 * camino sale del `dismiss`:
 *   - confirmar -> SOLICITAR      (abrir el formulario de cantidades)
 *   - cancelar  -> SIN_SOLICITUD  (seguir sin pedir nada)
 *   - X / Esc   -> CANCELAR       (volver al formulario, sin hacer nada)
 * El clic afuera queda deshabilitado a propósito: con tres caminos, un clic al voleo no puede
 * decidir por el usuario.
 *
 * Lista los faltantes como TEXTO y no como inputs —las cantidades se ajustan en el paso
 * siguiente—, para que la decisión no sea a ciegas.
 *
 * @returns {Promise<'solicitar'|'sin_solicitud'|'cancelar'>}
 */
export const preguntarSiSolicitaStock = (intl, faltantes) => {
	const contenedor = document.createElement('div');

	const leyenda = document.createElement('p');
	leyenda.textContent = intl.formatMessage({
		id: 'correctivosAbm.solicitudStock.pregunta_leyenda',
		defaultMessage: 'No hay stock suficiente para estos repuestos. ¿Querés generar una solicitud de stock?',
	});
	contenedor.appendChild(leyenda);

	const lista = document.createElement('ul');
	lista.style.textAlign = 'left';
	lista.style.display = 'inline-block';

	(faltantes || []).forEach((item) => {
		const fila = document.createElement('li');

		fila.textContent = item.descripcion + ' — ' + intl.formatMessage(
			{
				id: 'correctivosAbm.solicitudStock.pregunta_faltante',
				defaultMessage: 'faltan {falta} de {necesaria} (hay {disponible})',
			},
			{ falta: faltanteDe(item), necesaria: Number(item.cantidad || 0), disponible: item.stockDisponible }
		);

		lista.appendChild(fila);
	});

	contenedor.appendChild(lista);

	return swal({
		title: intl.formatMessage({
			id: 'correctivosAbm.solicitudStock.pregunta_titulo',
			defaultMessage: '¿Generar una solicitud de stock?',
		}),
		html: contenedor,
		type: 'question',
		customClass: 'swal-wide',
		allowOutsideClick: false,
		showCloseButton: true,
		showCancelButton: true,
		confirmButtonText: `<i style="margin-right:5px" class="fa fa-check-circle"></i>${intl.formatMessage({ id: 'correctivosAbm.solicitudStock.pregunta_si', defaultMessage: ' Sí, generar solicitud' })}`,
		cancelButtonText: `<i style="margin-right:5px" class="fa fa-arrow-circle-right"></i>${intl.formatMessage({ id: 'correctivosAbm.solicitudStock.pregunta_no', defaultMessage: ' No, crear sin solicitud' })}`,
		confirmButtonColor: '#0ABFBC',
		// Gris y no rojo: el cancelar de este modal NO es la salida segura sino un camino
		// alternativo hacia adelante. Mismo criterio que MovilesGridRow:327, el otro modal del
		// repo con la semántica invertida. Volver atrás es la X / Esc.
		cancelButtonColor: '#bfbcbc',
		focusConfirm: false,
	}).then((res) => {
		if (res.value) return SOLICITAR;

		return res.dismiss === swal.DismissReason.cancel ? SIN_SOLICITUD : CANCELAR;
	});
};

/**
 * VEC-3905 — EL ENVÍO, Y POR QUÉ NO ES IGUAL EN LAS DOS PANTALLAS
 *
 * El modal ahora puede PERSISTIR la solicitud, pero solo si el caller le pasa `enviar`. Lo hace el
 * PRESUPUESTO DETALLADO, donde el ticket ya existe: el POST va en el `preConfirm` con
 * `showLoaderOnConfirm`, así que el modal queda abierto con el spinner y, si falla, el error sale
 * adentro sin perder las cantidades cargadas — que era el pedido de Ivan del 11/09.
 *
 * El ALTA DEL CORRECTIVO no le pasa `enviar`: ahí el ticket todavía no existe cuando se cargan las
 * cantidades y `item_stock_solicitudes.ticket_id` es una FK NOT NULL. El modal solo recolecta y el
 * POST lo hace `CorrectivosAbm::enviarSolicitudStock()` recién con el id del ticket creado.
 *
 * ⚠️ `enviar` NO PUEDE RECHAZAR HACIA AFUERA. Ver el detalle en el `preConfirm`: en la 7.33 una
 * promesa que rechaza cierra el modal en vez de dejarlo abierto.
 *
 */
/**
 * El formulario de cantidades.
 *
 * LISTA TODOS LOS REPUESTOS, no solo los que faltan: la solicitud es "lo que necesito para este
 * trabajo", así que el usuario ajusta el pedido completo en un solo lugar. Los que están en falta
 * van marcados para que se distingan de un vistazo, y cada cantidad viene PRECARGADA con el
 * faltante — lo que se necesita menos lo disponible—, que es lo que hay que pedir. Los que ya
 * tienen stock suficiente arrancan en 0.
 *
 * @param {object} intl
 * @param {Array} items repuestos ya agregados (sin mano de obra)
 * @param {string} textoConfirmar etiqueta del botón que sigue adelante, propia de cada pantalla
 * @returns {Promise<Array<{item, descripcion, cantidad}>|null>} null si el usuario canceló
 */
export const pedirCantidadesSolicitud = (intl, items, textoConfirmar, opciones) => {
	const { enviar, yaSolicitados } = opciones || {};
	const pedidos = yaSolicitados || {};

	const contenedor = document.createElement('div');

	const leyenda = document.createElement('p');
	leyenda.textContent = intl.formatMessage({
		id: 'correctivosAbm.solicitudStock.leyenda',
		defaultMessage: 'No hay stock suficiente para todos los repuestos. Revisá las cantidades a solicitar antes de continuar.',
	});
	contenedor.appendChild(leyenda);

	const tabla = document.createElement('table');
	tabla.className = 'table table-sm';

	const thead = document.createElement('thead');
	const filaTitulos = document.createElement('tr');
	[
		intl.formatMessage({ id: 'Repuestos', defaultMessage: 'Repuestos' }),
		intl.formatMessage({ id: 'correctivosAbm.repuestos.columna_stock', defaultMessage: 'Stock' }),
		intl.formatMessage({ id: 'correctivosAbm.solicitudStock.columna_solicitar', defaultMessage: 'A solicitar' }),
	].forEach((titulo) => {
		const th = document.createElement('th');
		th.textContent = titulo;
		filaTitulos.appendChild(th);
	});
	thead.appendChild(filaTitulos);
	tabla.appendChild(thead);

	const tbody = document.createElement('tbody');
	const inputs = [];
	// Renglones que EDITAN una línea existente (PUT), separados de los que la crean (POST).
	const ediciones = [];

	(items || []).forEach((item) => {
		const fila = document.createElement('tr');

		const celdaItem = document.createElement('td');
		celdaItem.textContent = item.descripcion;
		celdaItem.style.textAlign = 'left';
		fila.appendChild(celdaItem);

		const disponible = item.stockDisponible;

		const celdaStock = document.createElement('td');
		celdaStock.textContent =
			disponible === null || disponible === undefined ? '-' : String(disponible);
		if (tieneFaltante(item)) celdaStock.style.color = '#FF4558';
		fila.appendChild(celdaStock);

		const celdaCantidad = document.createElement('td');
		const pedido = pedidos[item.id];

		// VEC-3905 — UN REPUESTO QUE YA TIENE SOLICITUD ABIERTA SE EDITA, NO SE VUELVE A PEDIR.
		//
		// No puede haber dos solicitudes vigentes de la misma combinación ticket + repuesto, así que
		// este renglón no viaja en el POST: viaja en un PUT sobre la línea que ya existe. Es el
		// Scenario 01 de la card — "ofrece acceder a la solicitud existente para editarla".
		//
		// EL PISO SALE DEL DATO, NO DE UNA REGLA COPIADA. `min` es lo ya despachado (o 1 si no se
		// despachó nada): no se puede pedir menos de lo que el depósito ya entregó. El backend lo
		// valida igual — esto evita el error, no lo reemplaza.
		if (pedido) {
			const piso = Math.max(Number(pedido.cantidadDespachada) || 0, 1);

			const inputEdicion = document.createElement('input');
			inputEdicion.type = 'number';
			inputEdicion.min = String(piso);
			inputEdicion.step = '1';
			inputEdicion.className = 'form-control form-control-sm';
			inputEdicion.value = String(pedido.cantidad);
			inputEdicion.dataset.detalleId = String(pedido.detalleId);
			celdaCantidad.appendChild(inputEdicion);

			const marca = document.createElement('div');
			marca.style.fontSize = '11px';
			marca.textContent = Number(pedido.cantidadDespachada) > 0
				? intl.formatMessage(
					{
						id: 'solicitudStock.ya_solicitado_con_despacho',
						defaultMessage: 'Ya solicitado: {cantidad} · entregado: {despachado}',
					},
					{ cantidad: pedido.cantidad, despachado: pedido.cantidadDespachada }
				)
				: intl.formatMessage(
					{
						id: 'solicitudStock.ya_solicitado',
						defaultMessage: 'Ya solicitado: {cantidad}',
					},
					{ cantidad: pedido.cantidad }
				);
			celdaCantidad.appendChild(marca);

			fila.appendChild(celdaCantidad);

			ediciones.push({
				item: item,
				input: inputEdicion,
				detalleId: pedido.detalleId,
				cantidadOriginal: Number(pedido.cantidad),
				piso: piso,
			});
			tbody.appendChild(fila);

			return;
		}


		const input = document.createElement('input');
		input.type = 'number';
		input.min = '0';
		// Un envase es indivisible: `comprometerPorPresupuesto()` reserva con `ceil()`, así que pedir
		// 1,5 no significa nada. El `step` lo señala en el control y el `preConfirm` lo rechaza.
		input.step = '1';
		input.className = 'form-control form-control-sm';
		input.value = String(faltanteDe(item));
		input.dataset.itemId = String(item.id);
		celdaCantidad.appendChild(input);
		fila.appendChild(celdaCantidad);

		inputs.push({ item: item, input: input });
		tbody.appendChild(fila);
	});

	tabla.appendChild(tbody);
	contenedor.appendChild(tabla);

	// Hay repuestos marcados: hay que decir por qué no se pueden pedir y dónde se ajustan. La marca
	// sola ("Ya solicitado: 3") informa pero deja al usuario sin saber qué hacer con eso.
	//
	// Si además NO queda ningún renglón editable, el modal tampoco ofrece confirmar (ver
	// `showConfirmButton`): un botón que no puede hacer nada es peor que su ausencia.
	const hayMarcados = (items || []).some((item) => item && pedidos[item.id]);

	if (hayMarcados) {
		const nota = document.createElement('p');
		nota.style.marginTop = '10px';
		nota.style.fontSize = '12px';
		nota.textContent = inputs.length === 0
			? intl.formatMessage({
				id: 'solicitudStock.todo_ya_solicitado',
				defaultMessage: 'Ya hay una solicitud pendiente de todos estos repuestos para este ticket. Ajustá lo pedido desde el botón "Solicitudes de stock" del ticket.',
			})
			: intl.formatMessage({
				id: 'solicitudStock.algunos_ya_solicitados',
				defaultMessage: 'Los repuestos marcados ya tienen una solicitud pendiente para este ticket. Ajustá lo pedido desde el botón "Solicitudes de stock" del ticket.',
			});
		contenedor.appendChild(nota);
	}

	return swal({
		// EL TÍTULO DICE LO QUE EL MODAL HACE EN CADA CASO. Con una solicitud ya activa, "Hay que
		// crear una solicitud de stock" es falso: lo que hay que hacer es revisarla.
		title: hayMarcados
			? intl.formatMessage({
				id: 'solicitudStock.titulo_revisar',
				defaultMessage: 'Revisá la solicitud de stock de este ticket',
			})
			: intl.formatMessage({
				id: 'correctivosAbm.solicitudStock.titulo',
				defaultMessage: 'Hay que crear una solicitud de stock',
			}),
		html: contenedor,
		type: 'warning',
		customClass: 'swal-wide',
		allowOutsideClick: false,
		showCancelButton: true,
		cancelButtonText: `<i style="margin-right:5px" class="fa fa-times-circle"></i>${intl.formatMessage({ id: 'cancelar', defaultMessage: ' Cancelar' })}`,
		confirmButtonText: `<i style="margin-right:5px" class="fa fa-check-circle"></i>${textoConfirmar}`,
		cancelButtonColor: '#FF3145',
		confirmButtonColor: '#0ABFBC',
		focusConfirm: false,
		// Sin nada que crear ni que editar no hay nada que confirmar: queda solo la salida.
		showConfirmButton: inputs.length > 0 || ediciones.length > 0,
		// Mantiene el modal abierto con el spinner mientras el POST viaja. Sin esto, swal cierra
		// apenas se aprieta confirmar y el usuario no se entera de si la solicitud se envió o falló
		// — que es justamente lo que había que resolver (pedido de Ivan, 11/09).
		showLoaderOnConfirm: !!enviar,
		/**
		 * VEC-3319 Sc. 04 — no se confirma una solicitud sin una cantidad válida.
		 *
		 * ⚠️ NO SE VALIDA "CADA LÍNEA > 0", Y NO ES UN OLVIDO. El formulario lista TODOS los
		 * repuestos y precarga el faltante, así que los que ya tienen stock arrancan en 0: ahí el
		 * 0 significa "de este no pido nada" y es la respuesta correcta. Lo que el escenario
		 * quiere evitar es confirmar sin pedir nada, que se lee sobre el conjunto y no sobre el
		 * campo. Por lo mismo el input conserva `min='0'`.
		 *
		 * `showValidationMessage()` + `return false` es la forma de la v7.33: sin
		 * `expectRejections`, devolver `false` es lo que le dice a SweetAlert que no cierre
		 * (`sweetalert2.js:2118`). El modal queda abierto con las cantidades cargadas, que es el
		 * punto — perderlas obligaría a recargar todo el pedido. `showValidationError()` está
		 * deprecado en esta misma versión, así que no se usa.
		 *
		 * Acá es donde la card que persiste la solicitud va a colgar su POST, con
		 * `showLoaderOnConfirm: true`: esta validación corre antes y le deja el gancho puesto.
		 */
		preConfirm: () => {
			// El campo vacío se mira ANTES de convertir: `Number('')` es 0, que es un valor válido
			// —"de este no pido nada"— y sin este chequeo un campo borrado a mano se confundía con
			// un cero deliberado y salía el mensaje equivocado.
			const hayVacios = inputs.some((fila) => String(fila.input.value).trim() === '');
			const cantidades = inputs.map((fila) => Number(fila.input.value));
			const hayInvalidas = cantidades.some(
				(cantidad) => !Number.isInteger(cantidad) || cantidad < 0
			);

			if (hayVacios || hayInvalidas) {
				swal.showValidationMessage(
					intl.formatMessage({
						id: 'solicitudStock.error.cantidad_invalida',
						defaultMessage: 'Las cantidades tienen que ser números enteros mayores o iguales a cero.',
					})
				);

				return false;
			}

			// Las ediciones tienen su propio piso: no se puede dejar el pedido por debajo de lo que el
			// depósito ya entregó. El backend lo rechaza igual; acá se evita el viaje.
			const edicionInvalida = ediciones.some((fila) => {
				const valor = String(fila.input.value).trim();

				if (valor === '') return true;

				const cantidad = Number(valor);

				return !Number.isInteger(cantidad) || cantidad < fila.piso;
			});

			if (edicionInvalida) {
				swal.showValidationMessage(
					intl.formatMessage({
						id: 'solicitudStock.error.edicion_invalida',
						defaultMessage: 'De los repuestos ya solicitados no podés pedir menos de lo que ya te entregaron.',
					})
				);

				return false;
			}

			// "Algo que hacer" ahora son DOS cosas: pedir algo nuevo, o cambiar una cantidad ya pedida.
			// Confirmar sin ninguna de las dos no manda nada y cerraría el modal sin efecto.
			const pideAlgo = cantidades.some((cantidad) => cantidad > 0);
			const cambiaAlgo = edicionesCargadas().length > 0;

			if (!pideAlgo && !cambiaAlgo) {
				swal.showValidationMessage(
					intl.formatMessage({
						id: 'solicitudStock.error.sin_cantidades',
						defaultMessage: 'Hay que pedir al menos un repuesto para poder continuar.',
					})
				);

				return false;
			}

			// Sin `enviar` el modal solo recolecta y el caller decide qué hacer, que es como nació y
			// como lo sigue usando el alta del correctivo — ahí el ticket todavía no existe, así que no
			// hay contra qué postear hasta después de crearlo.
			if (!enviar) return true;

			// ⚠️ `enviar` NO PUEDE RECHAZAR HACIA AFUERA. En sweetalert2 7.33, sin `expectRejections`,
			// una promesa de `preConfirm` que rechaza dispara `errorWith()` y REJECTA la promesa del
			// modal (sweetalert2.js:2123), en vez de dejarlo abierto: el usuario vería el modal cerrarse
			// y perdería las cantidades. Por eso el catch va acá adentro.
			//
			// Y devuelve `false` LITERAL, no algo falsy: la comparación de la librería es estricta
			// (`preConfirmValue === false`), y es la única forma de mantener el modal abierto con el
			// mensaje de error y las cantidades cargadas.
			return enviar(cantidadesCargadas(), edicionesCargadas()).then(
				() => true,
				(error) => {
					swal.showValidationMessage(
						mensajeDeError(intl, error)
					);

					return false;
				}
			);
		},
	}).then((res) => {
		// Cancelar vuelve al formulario con todo cargado y sin seguir adelante: es la salida para
		// corregir las cantidades.
		if (!res.value) return null;

		return cantidadesCargadas();
	});

	/**
	 * Lo cargado en el formulario. Solo los renglones EDITABLES: los que ya tenían solicitud abierta
	 * nunca entraron a `inputs`, así que no viajan ni al `enviar` ni al caller.
	 */
	/**
	 * Las líneas YA EXISTENTES cuya cantidad cambió.
	 *
	 * Solo las que cambiaron: mandar un PUT con el mismo número sería una escritura inútil que además
	 * puede fallar sola —si el depósito despachó mientras el modal estaba abierto, el backend rechaza
	 * una cantidad que quedó por debajo de lo entregado— y el usuario no tocó nada.
	 */
	function edicionesCargadas() {
		return ediciones
			.filter((fila) => Number(fila.input.value) !== fila.cantidadOriginal)
			.map((fila) => ({
				detalleId: fila.detalleId,
				item: fila.item.id,
				descripcion: fila.item.descripcion,
				cantidad: Number(fila.input.value),
			}));
	}

	function cantidadesCargadas() {
		return inputs.map((fila) => ({
			item: fila.item.id,
			descripcion: fila.item.descripcion,
			cantidad: fila.input.value === '' ? 0 : Number(fila.input.value),
		}));
	}
};

/**
 * El texto que ve el usuario cuando el envío falla, dentro del propio modal.
 *
 * El backend manda la key de traducción en `mensaje` y los valores a interpolar en `context` — es el
 * contrato de `errorConContexto()` del controller, que existe justamente para que el 400 de
 * "ya hay una solicitud pendiente" o el de "no podés pedir menos de lo que ya te entregaron"
 * lleguen con sus números.
 *
 * Se traduce con `translateBackendKey()` y no a mano: es el helper que ya resuelve ese shape contra
 * `backendStrings`, con ICU cuando hay `intl`. Si la key no existe devuelve la key cruda, así que
 * ese caso también se atrapa — mostrar `inventarioSolicitudService.errors.algo` es peor que un
 * mensaje genérico.
 *
 * Un 500 o un corte de red no traen ese shape y caen al genérico, en vez de pintar `[object Object]`.
 */
const mensajeDeError = (intl, error) => {
	const cuerpo = (error && error.response && error.response.data) || {};
	const key = cuerpo.mensaje;

	if (key) {
		const texto = translateBackendKey(key, cuerpo.context, intl);

		if (texto && texto !== key) return texto;
	}

	return intl.formatMessage({
		id: 'solicitudStock.error.envio',
		defaultMessage: 'No se pudo enviar la solicitud de stock. Volvé a intentar.',
	});
};
